import { ListGangsResponse } from '../api/gangsApi';
import { UserSummaryDto } from '../../users/api/userManagementApi';
import {
  GangCreationFormData,
  GangParticipant,
} from '../types/gangCreationTypes';

const buildFullName = (
  givenName?: string | null,
  familyName?: string | null
) => {
  const given = (givenName ?? '').trim();
  const family = (familyName ?? '').trim();
  const combined = `${given} ${family}`.trim();
  return combined || 'Unknown Member';
};

const buildDirectoryMap = (users?: UserSummaryDto[]) => {
  if (!users || users.length === 0) {
    return new Map<string, UserSummaryDto>();
  }

  return users.reduce<Map<string, UserSummaryDto>>((accumulator, user) => {
    accumulator.set(user.id, user);
    return accumulator;
  }, new Map());
};

export const mapGangToFormData = (
  gang: ListGangsResponse['gangs'][number],
  organisationUsers?: UserSummaryDto[]
): GangCreationFormData => {
  const userDirectory = buildDirectoryMap(organisationUsers);
  const participants: Record<string, GangParticipant> = {};
  const leaderIds: string[] = [];
  const memberIds: string[] = [];

  gang.members.forEach(member => {
    const directoryUser = userDirectory.get(member.userId.toString());

    // Determine payment type based on which field is populated
    const hasRate = member.defaultRate != null;
    const paymentType = hasRate
      ? ('fixedRate' as const)
      : ('percentage' as const);

    // API returns 0-1 format (0.333), form uses 0-100 format (33.3)
    // Multiply by 100 and round to 1 d.p. for display
    const participant: GangParticipant = {
      userId: member.userId,
      fullName:
        buildFullName(member.givenName, member.familyName) ||
        directoryUser?.fullName ||
        'Unknown Member',
      email: member.email ?? directoryUser?.email ?? '',
      canSeeSplit: member.isLeader ? true : Boolean(member.canSeeSplit),
      paymentType,
      defaultSplitPercentage:
        member.defaultSplitPercentage !== null &&
        member.defaultSplitPercentage !== undefined
          ? Math.round(member.defaultSplitPercentage * 100 * 10) / 10
          : null,
      defaultRate:
        member.defaultRate !== null && member.defaultRate !== undefined
          ? member.defaultRate
          : null,
      defaultUnitsValue:
        member.defaultUnitsValue !== null &&
        member.defaultUnitsValue !== undefined
          ? member.defaultUnitsValue
          : null,
    };

    participants[participant.userId] = participant;

    if (member.isLeader) {
      leaderIds.push(participant.userId);
    } else {
      memberIds.push(participant.userId);
    }
  });

  return {
    name: gang.name,
    isEqualSplit: gang.isEqualSplit,
    participants,
    leaderIds,
    memberIds,
  };
};
